/**
 * The player's ACT account as the Account panel shows it (DR-0053): guest or
 * Google, the Account ID, and linked Sui addresses. Supabase is the login
 * (Google ID tokens, `linkIdentity` for a guest so the account is kept in
 * place); the home game server makes the Account ID and checks "Link Sui"
 * signatures. Rows come straight from `account_sui_addresses` (owner read).
 */
import type { SupabaseClient } from "@supabase/supabase-js";
import type { AccountErrorCode, AccountIdResponse, ChainRefreshResponse, SuiLinkRequest, SuiLinkResponse, SuiAddressKind } from "@act/shared";
import { resolveColyseusEndpoint } from "../network/networkConfig";
import { TypedEvent } from "../utils/TypedEvent";

export type AccountView = {
  /** Signed in with Google (false for a guest or when offline). */
  signedIn: boolean;
  email: string | null;
  /** The Supabase user id: named in the Link Sui message. */
  userId: string | null;
  accountId: string | null;
  /** Why a Google account has no Account ID yet: it needs a fresh Google sign-in, or the server can't make one yet. */
  accountIdWaiting: "sign_in" | "unavailable" | null;
  linked: string[];
  /** How many of the game's items the account's Sui addresses hold (ACT HQ t-042); null until read. */
  walletItems: number | null;
};

export type AccountFailure = AccountErrorCode | "offline" | "google_failed";
export type AccountResult<T = object> = ({ ok: true } & T) | { ok: false; error: AccountFailure };

const SIGNED_OUT: AccountView = { signedIn: false, email: null, userId: null, accountId: null, accountIdWaiting: null, linked: [], walletItems: null };

export class AccountService {
  readonly onChanged = new TypedEvent<AccountView>();
  /** The account's entitlements changed after a chain read: read them again. */
  readonly onHoldingsChanged = new TypedEvent<string[]>();
  private view: AccountView = SIGNED_OUT;
  /** What the last chain read found, while signed in to this account. */
  private holdings: { userId: string; items: string[] } | null = null;

  constructor(
    private readonly supabase: SupabaseClient | null,
    private readonly serverUrl: () => string = resolveColyseusEndpoint,
  ) {}

  current(): AccountView {
    return this.view;
  }

  /** Re-read the session and this account's addresses. */
  async refresh(): Promise<AccountView> {
    const sb = this.supabase;
    const session = sb ? (await sb.auth.getSession()).data.session : null;
    if (!sb || !session) return this.publish(SIGNED_OUT);
    const user = session.user;
    const { data } = await sb.from("account_sui_addresses").select("address, kind");
    const rows = (data ?? []) as Array<{ address: string; kind: SuiAddressKind }>;
    const signedIn = user.is_anonymous !== true;
    let accountId = rows.find((r) => r.kind === "account")?.address ?? null;
    let accountIdWaiting: AccountView["accountIdWaiting"] = null;
    if (signedIn && !accountId) {
      // Ask without a token: the server says whether it could make one with a fresh Google sign-in.
      const probe = await this.makeAccountId();
      if (probe.ok) accountId = probe.accountId;
      else accountIdWaiting = probe.error === "account_id_unavailable" ? "unavailable" : "sign_in";
    }
    return this.publish({
      signedIn,
      email: user.email || null,
      userId: user.id,
      accountId,
      accountIdWaiting,
      linked: rows.filter((r) => r.kind === "linked").map((r) => r.address).sort(),
      walletItems: this.holdings?.userId === user.id ? this.holdings.items.length : null,
    });
  }

  /**
   * Read what the account's Sui addresses hold into its entitlements (the chain
   * reader, ACT HQ t-042, DR-0088). The server reads the Account ID and every
   * linked address; a big holder can take a few seconds, so callers don't wait.
   */
  async refreshHoldings(): Promise<AccountResult<{ items: string[]; changed: boolean }>> {
    const sb = this.supabase;
    const session = sb ? (await sb.auth.getSession()).data.session : null;
    if (!session || session.user.is_anonymous) return { ok: false, error: "sign_in_first" };
    const response = await this.post<ChainRefreshResponse>("/account/chain/refresh", {});
    if (!response.ok) return response;
    const { items, changed } = response.body;
    if (!Array.isArray(items) || !items.every((id) => typeof id === "string")) return { ok: false, error: "unavailable" };
    this.holdings = { userId: session.user.id, items };
    if (this.view.userId === session.user.id) this.publish({ ...this.view, walletItems: items.length });
    if (changed) this.onHoldingsChanged.emit(items);
    return { ok: true, items, changed };
  }

  /**
   * Sign in with a Google ID token. A guest keeps its account: the Google login
   * is linked to it in place. If that Google login already has an account, the
   * existing account wins and this browser switches to it (`switched`; the page
   * must reload, since everything was wired to the old account id). Then the
   * server makes the Account ID from the same token.
   */
  async signInWithGoogle(idToken: string, rawNonce: string): Promise<AccountResult<{ switched: boolean }>> {
    const sb = this.supabase;
    if (!sb) return { ok: false, error: "offline" };
    const before = (await sb.auth.getSession()).data.session;
    let switched = false;
    if (before?.user.is_anonymous) {
      const linked = await sb.auth.linkIdentity({ provider: "google", token: idToken, nonce: rawNonce });
      if (linked.error) {
        if (linked.error.code !== "identity_already_exists") {
          console.warn("[account] google link failed:", linked.error.code ?? linked.error.message);
          return { ok: false, error: "google_failed" };
        }
        const signed = await sb.auth.signInWithIdToken({ provider: "google", token: idToken, nonce: rawNonce });
        if (signed.error) {
          console.warn("[account] google sign-in failed:", signed.error.code ?? signed.error.message);
          return { ok: false, error: "google_failed" };
        }
        switched = true;
      }
    } else {
      const signed = await sb.auth.signInWithIdToken({ provider: "google", token: idToken, nonce: rawNonce });
      if (signed.error) {
        console.warn("[account] google sign-in failed:", signed.error.code ?? signed.error.message);
        return { ok: false, error: "google_failed" };
      }
      switched = signed.data.user?.id !== before?.user.id;
    }
    // A failure here leaves the account signed in without an Account ID; the
    // panel offers Google again, since only a fresh token can make one.
    await this.makeAccountId(idToken);
    await this.refresh();
    if (!switched) void this.refreshHoldings(); // a switch reloads the page, which reads them
    return { ok: true, switched };
  }

  /** Ask the server for the Account ID (made from `idToken` if it doesn't exist yet). */
  async makeAccountId(idToken?: string): Promise<AccountResult<{ accountId: string }>> {
    const response = await this.post<AccountIdResponse>("/account/id", idToken ? { idToken } : {});
    if (!response.ok) return response;
    return { ok: true, accountId: response.body.accountId };
  }

  /** Add a Sui address whose app signed the link message. */
  async linkSui(request: SuiLinkRequest): Promise<AccountResult<{ moved: boolean }>> {
    const response = await this.post<SuiLinkResponse>("/account/sui/link", request);
    if (!response.ok) return response;
    await this.refresh();
    void this.refreshHoldings();
    return { ok: true, moved: response.body.moved };
  }

  /** Remove a linked address (never the Account ID: the database refuses that). */
  async unlink(address: string): Promise<boolean> {
    const sb = this.supabase;
    if (!sb) return false;
    const { error } = await sb.from("account_sui_addresses").delete().eq("address", address).eq("kind", "linked");
    await this.refresh();
    if (!error) void this.refreshHoldings();
    return !error;
  }

  /** Sign this browser out (other devices stay signed in). The caller reloads: the next boot is a fresh guest. */
  async signOut(): Promise<void> {
    await this.supabase?.auth.signOut({ scope: "local" });
  }

  private async post<T>(path: string, body: unknown): Promise<{ ok: true; body: T } | { ok: false; error: AccountFailure }> {
    const sb = this.supabase;
    const token = sb ? (await sb.auth.getSession()).data.session?.access_token : null;
    if (!token) return { ok: false, error: "offline" };
    try {
      const response = await fetch(`${this.serverUrl()}${path}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(body),
        cache: "no-store",
      });
      const json = (await response.json().catch(() => ({}))) as T & { error?: AccountErrorCode };
      if (!response.ok) return { ok: false, error: json.error ?? "unavailable" };
      return { ok: true, body: json };
    } catch {
      return { ok: false, error: "unavailable" };
    }
  }

  private publish(view: AccountView): AccountView {
    this.view = view;
    this.onChanged.emit(view);
    return view;
  }
}
